import { useEffect, useState } from "react";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Trash, Plus } from "@phosphor-icons/react";

const PRIORITIES = ["low", "medium", "high"];
const TYPES = ["study", "practice", "review"];
const DOMAINS = ["", "People", "Process", "Business Environment"];

export default function Tasks() {
  const { t, tDomain } = useT();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", type: "study", domain: "", priority: "medium", deadline: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (tk) => {
    await api.patch(`/tasks/${tk.task_id}`, { completed: !tk.completed });
    load();
  };
  const remove = async (tk) => {
    await api.delete(`/tasks/${tk.task_id}`);
    load();
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...form, domain: form.domain || null, deadline: form.deadline || null });
      setForm({ title: "", description: "", type: "study", domain: "", priority: "medium", deadline: "" });
      setShowCreate(false);
      load();
      toast.success(t("tasks.added"));
    } catch {
      toast.error(t("tasks.create_failed"));
    }
  };

  const open = tasks.filter((tk) => !tk.completed);
  const done = tasks.filter((tk) => tk.completed);

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#002FA7]">{t("tasks.overline")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("tasks.title")}</h1>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary" data-testid="tasks-add-button"><Plus size={16} className="inline mr-1" /> {t("tasks.add")}</button>
      </div>

      {showCreate && (
        <form onSubmit={create} className="card mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tasks-create-form">
          <div>
            <label className="overline">{t("common.title")}</label>
            <input value={form.title} required onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
          </div>
          <div>
            <label className="overline">{t("common.type")}</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="task-type-select">
              {TYPES.map((tt) => <option key={tt}>{tt}</option>)}
            </select>
          </div>
          <div>
            <label className="overline">{t("common.domain")}</label>
            <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="task-domain-select">
              {DOMAINS.map((d) => <option key={d} value={d}>{d ? tDomain(d) : "—"}</option>)}
            </select>
          </div>
          <div>
            <label className="overline">{t("common.priority")}</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority-select">
              {PRIORITIES.map((p) => <option key={p} value={p}>{t(`common.${p}`)}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline">{t("common.description")}</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="task-description-input" />
          </div>
          <div>
            <label className="overline">{t("common.deadline")}</label>
            <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline-input" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary" data-testid="task-create-submit">{t("common.create")}</button>
            <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      <section className="mt-8">
        <div className="overline">{t("common.open")} · {open.length}</div>
        <div className="mt-2 space-y-2" data-testid="tasks-open-list">
          {open.length === 0 && <div className="text-sm text-slate-500">{t("tasks.all_caught_up")}</div>}
          {open.map((tk) => <TaskRow key={tk.task_id} t={tk} onToggle={toggle} onRemove={remove} tDomain={tDomain} tT={t} />)}
        </div>
      </section>
      <section className="mt-10">
        <div className="overline">{t("common.completed")} · {done.length}</div>
        <div className="mt-2 space-y-2" data-testid="tasks-done-list">
          {done.length === 0 && <div className="text-sm text-slate-500">{t("tasks.no_completed")}</div>}
          {done.map((tk) => <TaskRow key={tk.task_id} t={tk} onToggle={toggle} onRemove={remove} tDomain={tDomain} tT={t} />)}
        </div>
      </section>
    </AppShell>
  );
}

function TaskRow({ t, onToggle, onRemove, tDomain, tT }) {
  const overdue = t.deadline && !t.completed && new Date(t.deadline) < new Date();
  const displayTitle = t.title_key ? tT(t.title_key, { domain: t.domain ? tDomain(t.domain) : "" }) : t.title;
  const displayDesc = t.description_key ? tT(t.description_key) : t.description;
  return (
    <div className="card !p-4 flex items-start gap-3">
      <input type="checkbox" className="!w-4 !h-4 mt-1" checked={t.completed} onChange={() => onToggle(t)} data-testid={`task-checkbox-${t.task_id}`} />
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${t.completed ? "line-through text-slate-400" : ""}`}>{displayTitle}</div>
        {displayDesc && <div className="text-sm text-slate-500 mt-1">{displayDesc}</div>}
        <div className="text-xs text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${t.priority === "high" ? "bg-[#FFE4E6] text-[#E11D48]" : t.priority === "low" ? "bg-slate-100 text-slate-500" : "bg-[#FEF3C7] text-[#D97706]"}`}>{tT(`common.${t.priority}`)}</span>
          {t.domain && <span>{tDomain(t.domain)}</span>}
          <span>· {t.type}</span>
          {t.deadline && <span className={overdue ? "text-[#E11D48] font-semibold" : ""}>· {tT("tasks.due")} {new Date(t.deadline).toLocaleDateString()}{overdue ? ` · ${tT("tasks.overdue")}` : ""}</span>}
        </div>
      </div>
      <button className="text-slate-400 hover:text-[#E11D48]" onClick={() => onRemove(t)} data-testid={`task-delete-${t.task_id}`}><Trash size={16} /></button>
    </div>
  );
}
